import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ExternalLink, Plug, RefreshCw, X } from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge, type BadgeVariant } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/Spinner';
import { listAccounts } from '../accounts/api';
import { ApiError } from '../../lib/api';
import type { Account } from '../../types/account';
import type { AccountOnboarding, OnboardingBatchResult } from '../../types/accountOnboarding';
import {
  displayStatus,
  findOpenOnboardingForAccount,
  isAccountConnectable,
  isOnboardingListable,
  nextAction,
  ONBOARDING_STATUS_LABEL,
  sortOnboardings,
  type OnboardingDisplayStatus,
} from '../../lib/onboardingFlow';
import { useDebouncedValue } from '../metaAds/useDebouncedValue';
import {
  createOnboardingBatch,
  finalizeOnboarding,
  getConnectUrl,
  getOnboardingAdAccounts,
  getOnboardingPages,
  getOnboardingResources,
  listOnboardings,
  PaymentRequiredError,
  reconnectOnboarding,
  selectOnboardingAdAccount,
  selectOnboardingPage,
  TokenExpiredError,
  type FinalizeBody,
} from './api';

// Account Onboarding wizard — ACCOUNT-FIRST model. Per row:
//   0. Pick the EXISTING kyck Account(s) to connect (already-connected
//      accounts are greyed — managed from the account page)
//   1. Create — a Zernio profile per selected account (name defaults to the
//      account name, editable per row before create)
//   2. Connect — Meta OAuth (HEADLESS: only the consent happens at Meta; the
//      callback returns through /onboarding/callback and kyck fetches the page
//      list server-side)
//   2b. Select Facebook Page — kyck's own searchable picker (replaces Zernio's
//      hosted selection UI, which timed out on agency tokens with 1000+ pages)
//   3. Select & finalize — scope the club's ad account, capture page/IG/
//      pixel/lead-form defaults, then UPDATE the account with the Zernio link
// Rows are independent and resumable; a second entry for the same account
// resumes its open record instead of duplicating. The account page deep-links
// here with ?account=:id preselected.

const STATUS_VARIANT: Record<OnboardingDisplayStatus, BadgeVariant> = {
  created: 'neutral',
  awaiting_page_selection: 'info',
  connected: 'info',
  scoped: 'warning',
  complete: 'success',
  failed: 'danger',
};

type PaymentBlock = { message: string; reason: string; details: Record<string, unknown> | null };

function paymentBlockFrom(err: unknown): PaymentBlock | null {
  if (err instanceof PaymentRequiredError) {
    return { message: err.message, reason: err.reason, details: err.details };
  }
  return null;
}

function paymentBlockSummary(block: PaymentBlock): string {
  const d = block.details ?? {};
  const count = typeof d.current_account_count === 'number' ? d.current_account_count : null;
  const limit =
    typeof d.free_tier_account_limit === 'number'
      ? d.free_tier_account_limit
      : typeof d.effective_account_limit === 'number'
        ? d.effective_account_limit
        : typeof d.public_account_limit === 'number'
          ? d.public_account_limit
          : null;
  if (count !== null && limit !== null) return `${count} of ${limit} connected accounts used.`;
  if (count !== null) return `${count} connected accounts in use.`;
  return '';
}

export function OnboardingPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get('focus');
  const preselectAccountId = searchParams.get('account');
  const reconnectAccountId = searchParams.get('reconnect');
  const reconnectModeParam = searchParams.get('mode');

  const [paymentBlock, setPaymentBlock] = useState<PaymentBlock | null>(null);
  // Surfaced reconnect failures (no silent dead-ends). offerPageReconnect is set
  // when an ad-account reconnect 409s because the Page isn't connected — the
  // banner then offers the page-reconnect action.
  const [reconnectError, setReconnectError] = useState<
    { accountId: string; message: string; offerPageReconnect: boolean } | null
  >(null);
  // Step 0 selection: accountId → editable profile name.
  const [selection, setSelection] = useState<Map<string, string>>(new Map());
  const [batchResults, setBatchResults] = useState<OnboardingBatchResult[] | null>(null);
  const [openRowId, setOpenRowId] = useState<string | null>(focusId);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);

  const onboardings = useQuery({
    queryKey: ['onboardings'],
    queryFn: listOnboardings,
    refetchOnWindowFocus: true,
  });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });

  const accountsById = useMemo(
    () => new Map((accounts.data ?? []).map((a) => [a.id, a])),
    [accounts.data],
  );

  // Deep link from the account page (?account=:id): resume the open record if
  // one exists, otherwise preselect the account in the step-0 picker.
  const deepLinked = useRef(false);
  useEffect(() => {
    if (deepLinked.current || !preselectAccountId) return;
    if (!onboardings.data || !accounts.data) return;
    deepLinked.current = true;
    const open = findOpenOnboardingForAccount(onboardings.data.onboardings, preselectAccountId);
    if (open) {
      setOpenRowId(open.id);
    } else {
      const account = accountsById.get(preselectAccountId);
      if (account && isAccountConnectable(account)) {
        setSelection((s) => new Map(s).set(account.id, account.name));
      }
    }
    setSearchParams({}, { replace: true });
  }, [preselectAccountId, onboardings.data, accounts.data, accountsById, setSearchParams]);

  // Deep link from the account page's drift "Reconnect" / metaads "Connect"
  // buttons (?reconnect=:id&mode=ad-account): create a fresh reconnect record
  // reusing the account's existing Zernio profile (server-side), then open it so
  // the ad-account picker is visible immediately. Fires once.
  const reconnect = useMutation({
    mutationFn: ({ accountId, mode }: { accountId: string; mode: 'ad-account' | 'page' }) =>
      reconnectOnboarding(accountId, mode),
    onSuccess: ({ onboarding }) => {
      setReconnectError(null);
      void queryClient.invalidateQueries({ queryKey: ['onboardings'] });
      setOpenRowId(onboarding.id);
    },
    onError: (err, variables) => {
      if (err instanceof PaymentRequiredError) {
        handleError(err);
        return;
      }
      const message = err instanceof Error ? err.message : 'Reconnect failed.';
      // A 409 on an ad-account reconnect means the Page isn't connected — offer
      // the page reconnect instead of leaving the user stuck.
      const offerPageReconnect =
        variables.mode === 'ad-account' && err instanceof ApiError && err.status === 409;
      setReconnectError({ accountId: variables.accountId, message, offerPageReconnect });
    },
  });
  const reconnected = useRef(false);
  useEffect(() => {
    if (reconnected.current || !reconnectAccountId) return;
    reconnected.current = true;
    const mode = reconnectModeParam === 'page' ? 'page' : 'ad-account';
    reconnect.mutate({ accountId: reconnectAccountId, mode });
    setSearchParams({}, { replace: true });
    // reconnect.mutate is stable; the ref guard ensures a single fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconnectAccountId, reconnectModeParam, setSearchParams]);

  function handleError(err: unknown, rowId?: string) {
    const pb = paymentBlockFrom(err);
    if (pb) {
      setPaymentBlock(pb);
      return;
    }
    const message = err instanceof Error ? err.message : 'Something went wrong.';
    if (rowId) {
      setRowError({ id: rowId, message });
      // An expired headless-connect token regressed the row to 'created'
      // server-side — reload so the Connect button reappears.
      if (err instanceof TokenExpiredError) {
        void queryClient.invalidateQueries({ queryKey: ['onboardings'] });
      }
    }
  }

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['onboardings'] });

  const createBatch = useMutation({
    mutationFn: (items: Array<{ accountId: string; profileName?: string | null }>) =>
      createOnboardingBatch(items),
    onSuccess: (res) => {
      setBatchResults(res.results);
      setSelection(new Map());
      const firstFailure = res.results.find((r) => r.outcome === 'failed' && r.error);
      if (res.results.some((r) => r.outcome === 'blocked') && firstFailure) {
        setPaymentBlock({ message: firstFailure.error!, reason: 'unknown', details: null });
      }
      void invalidate();
    },
    onError: (err) => handleError(err),
  });

  const connect = useMutation({
    mutationFn: (id: string) => getConnectUrl(id),
    onSuccess: ({ authUrl }) => {
      window.location.href = authUrl;
    },
    onError: (err, id) => handleError(err, id),
  });

  const rows = useMemo(
    () => sortOnboardings(onboardings.data?.onboardings ?? []),
    [onboardings.data],
  );

  const blocked = paymentBlock !== null;

  return (
    <div>
      <PageHeader
        title="Account Onboarding"
        description="Connect existing client accounts to Zernio and Meta so the Ad Builder can use them."
        actions={
          <div className="flex gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/onboarding/gbp-agency">Google Business (agency)</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/onboarding/bulk">Bulk onboarding</Link>
            </Button>
          </div>
        }
      />

      {paymentBlock && (
        <div className="mb-6 flex items-start gap-3 rounded-md border border-rose-200 bg-rose-50 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
          <div className="text-sm text-rose-800">
            <p className="font-semibold">Zernio account limit reached</p>
            <p className="mt-0.5">
              {paymentBlock.message} {paymentBlockSummary(paymentBlock)}
            </p>
            <p className="mt-1 text-rose-700">
              Remaining rows are blocked until the limit is raised — contact Miki / Zernio
              billing, then dismiss this banner to retry.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => setPaymentBlock(null)}
            >
              Dismiss & retry
            </Button>
          </div>
        </div>
      )}

      {reconnectError && (
        <div className="mb-6 flex items-start gap-3 rounded-md border border-rose-200 bg-rose-50 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
          <div className="text-sm text-rose-800">
            <p className="font-semibold">Couldn't reconnect</p>
            <p className="mt-0.5">{reconnectError.message}</p>
            <div className="mt-2 flex items-center gap-2">
              {reconnectError.offerPageReconnect && (
                <Button
                  size="sm"
                  onClick={() =>
                    reconnect.mutate({ accountId: reconnectError.accountId, mode: 'page' })
                  }
                  disabled={reconnect.isPending}
                >
                  <Plug className="h-4 w-4" />
                  Reconnect the page
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => setReconnectError(null)}>
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      )}

      <AccountPickerCard
        accounts={accounts.data ?? null}
        accountsLoading={accounts.isLoading}
        selection={selection}
        onSelectionChange={setSelection}
        onCreate={() =>
          createBatch.mutate(
            Array.from(selection.entries()).map(([accountId, profileName]) => ({
              accountId,
              profileName: profileName.trim() || null,
            })),
          )
        }
        creating={createBatch.isPending}
        blocked={blocked}
        batchResults={batchResults}
      />

      {/* Checklist */}
      <div className="mt-8 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">Onboarding checklist</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void onboardings.refetch()}
          disabled={onboardings.isFetching}
        >
          <RefreshCw className={onboardings.isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          Refresh
        </Button>
      </div>

      {onboardings.isLoading ? (
        <div className="mt-6 flex justify-center">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">
          No onboardings yet — pick an account above to start.
        </p>
      ) : (
        <div className="mt-3 divide-y divide-slate-100 rounded-md border border-slate-200">
          {rows.map((row) => (
            <OnboardingRow
              key={row.id}
              row={row}
              accountName={
                row.kyckAccountId ? (accountsById.get(row.kyckAccountId)?.name ?? null) : null
              }
              open={openRowId === row.id}
              onToggle={() => setOpenRowId(openRowId === row.id ? null : row.id)}
              onConnect={() => connect.mutate(row.id)}
              connecting={connect.isPending && connect.variables === row.id}
              blocked={blocked}
              rowError={rowError?.id === row.id ? rowError.message : null}
              onError={(err) => handleError(err, row.id)}
              onAdvanced={() => {
                setRowError(null);
                void invalidate();
                void queryClient.invalidateQueries({ queryKey: ['accounts'] });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Step 0: account picker (single + batch are one multi-select) ----------

function AccountPickerCard({
  accounts,
  accountsLoading,
  selection,
  onSelectionChange,
  onCreate,
  creating,
  blocked,
  batchResults,
}: {
  accounts: Account[] | null;
  accountsLoading: boolean;
  selection: Map<string, string>;
  onSelectionChange: (next: Map<string, string>) => void;
  onCreate: () => void;
  creating: boolean;
  blocked: boolean;
  batchResults: OnboardingBatchResult[] | null;
}) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Only Meta-eligible, non-cancelled accounts are offered (compose with the
    // already-connected greying below — non-eligible / cancelled accounts drop
    // out entirely, eligible-but-connected ones still show greyed).
    const all = (accounts ?? [])
      .filter(isOnboardingListable)
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
    if (!q) return all;
    return all.filter((a) => a.name.toLowerCase().includes(q));
  }, [accounts, search]);

  function toggle(account: Account) {
    const next = new Map(selection);
    if (next.has(account.id)) next.delete(account.id);
    else next.set(account.id, account.name);
    onSelectionChange(next);
  }

  const selectedEntries = Array.from(selection.entries());
  const accountsById = new Map((accounts ?? []).map((a) => [a.id, a]));

  return (
    <div className="max-w-2xl rounded-md border border-slate-200 p-4">
      <h2 className="text-sm font-semibold text-slate-900">Select accounts to connect</h2>
      <p className="mt-0.5 text-xs text-slate-500">
        Pick one or more existing kyck accounts. A Zernio profile is created per account — the
        profile name defaults to the account name (editable below).
      </p>
      <Input
        className="mt-3"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search accounts…"
        disabled={blocked}
      />
      {accountsLoading ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
          <Spinner className="h-4 w-4" /> Loading accounts…
        </div>
      ) : (
        <ul className="mt-2 max-h-56 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200">
          {filtered.map((a) => {
            const connectable = isAccountConnectable(a);
            return (
              <li key={a.id} className="flex items-center gap-3 px-3 py-1.5">
                {connectable ? (
                  <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selection.has(a.id)}
                      onChange={() => toggle(a)}
                      disabled={blocked}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-slate-900">{a.name}</span>
                      <span className="block text-xs text-slate-500">{a.accountType}</span>
                    </span>
                  </label>
                ) : (
                  <div className="flex min-w-0 flex-1 items-center gap-3 opacity-60">
                    <input type="checkbox" checked disabled />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-slate-500">{a.name}</span>
                      <span className="block text-xs text-slate-400">
                        Already connected —{' '}
                        <Link to={`/accounts/${a.id}`} className="underline pointer-events-auto">
                          manage from the account page
                        </Link>
                      </span>
                    </span>
                  </div>
                )}
              </li>
            );
          })}
          {filtered.length === 0 && (
            <li className="px-3 py-2 text-sm text-slate-500">No accounts match.</li>
          )}
        </ul>
      )}

      {selectedEntries.length > 0 && (
        <div className="mt-3 space-y-2">
          <p className="text-xs font-medium text-slate-700">
            Profile name per account (editable):
          </p>
          {selectedEntries.map(([accountId, profileName]) => (
            <div key={accountId} className="flex items-center gap-2">
              <span className="w-56 truncate text-xs text-slate-500">
                {accountsById.get(accountId)?.name ?? accountId}
              </span>
              <Input
                className="h-8 flex-1 text-sm"
                value={profileName}
                onChange={(e) =>
                  onSelectionChange(new Map(selection).set(accountId, e.target.value))
                }
                disabled={blocked || creating}
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => {
                  const next = new Map(selection);
                  next.delete(accountId);
                  onSelectionChange(next);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3">
        <Button onClick={onCreate} disabled={blocked || creating || selectedEntries.length === 0}>
          {creating
            ? 'Creating…'
            : `Create ${selectedEntries.length || ''} profile${selectedEntries.length === 1 ? '' : 's'}`}
        </Button>
      </div>

      {batchResults && (
        <ul className="mt-3 space-y-1 text-sm">
          {batchResults.map((r) => (
            <li key={r.accountId} className="flex items-center gap-2">
              <Badge
                variant={
                  r.outcome === 'created' || r.outcome === 'resumed'
                    ? 'success'
                    : r.outcome === 'blocked'
                      ? 'neutral'
                      : 'danger'
                }
              >
                {r.outcome}
              </Badge>
              <span className="text-slate-700">{r.name}</span>
              {r.error && <span className="text-rose-600">— {r.error}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OnboardingRow({
  row,
  accountName,
  open,
  onToggle,
  onConnect,
  connecting,
  blocked,
  rowError,
  onError,
  onAdvanced,
}: {
  row: AccountOnboarding;
  accountName: string | null;
  open: boolean;
  onToggle: () => void;
  onConnect: () => void;
  connecting: boolean;
  blocked: boolean;
  rowError: string | null;
  onError: (err: unknown) => void;
  onAdvanced: () => void;
}) {
  const status = displayStatus(row);
  const action = nextAction(row);
  const errorMessage = rowError ?? row.lastError?.message ?? null;

  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-900">
            {accountName ?? row.name}
          </p>
          <p className="truncate text-xs text-slate-500">
            {row.name !== accountName ? `Profile "${row.name}" · ` : ''}
            {row.metaUsername ? `Connected by ${row.metaUsername} · ` : ''}
            {row.adAccountId ? `${row.adAccountId} · ` : ''}
            {row.profileId}
          </p>
        </div>
        <Badge variant={STATUS_VARIANT[status]}>{ONBOARDING_STATUS_LABEL[status]}</Badge>
        {action === 'connect' && (
          <Button size="sm" onClick={onConnect} disabled={blocked || connecting}>
            <Plug className="h-4 w-4" />
            {connecting ? 'Opening…' : 'Connect Meta'}
          </Button>
        )}
        {(action === 'select-page' || action === 'select' || action === 'finalize') && (
          <Button size="sm" variant={open ? 'secondary' : 'default'} onClick={onToggle} disabled={blocked}>
            {open
              ? 'Close'
              : action === 'select-page'
                ? 'Select page'
                : action === 'select'
                  ? 'Select ad account'
                  : 'Finalize'}
          </Button>
        )}
        {action === 'done' && row.kyckAccountId && (
          <Button asChild size="sm" variant="outline">
            <Link to={`/accounts/${row.kyckAccountId}`}>
              View account
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </Button>
        )}
      </div>
      {status === 'failed' && errorMessage && (
        <p className="mt-1 text-xs text-rose-600">{errorMessage}</p>
      )}
      {rowError && status !== 'failed' && (
        <p className="mt-1 text-xs text-rose-600">{rowError}</p>
      )}
      {open && action === 'select-page' && (
        <PageSelectPanel
          row={row}
          accountName={accountName}
          blocked={blocked}
          onError={onError}
          onAdvanced={onAdvanced}
        />
      )}
      {open && (action === 'select' || action === 'finalize') && (
        <Step3Panel row={row} onError={onError} onAdvanced={onAdvanced} blocked={blocked} />
      )}
    </div>
  );
}

// ---------- Step 2b: headless Facebook Page picker (kyck-native) ----------

function PageSelectPanel({
  row,
  accountName,
  blocked,
  onError,
  onAdvanced,
}: {
  row: AccountOnboarding;
  accountName: string | null;
  blocked: boolean;
  onError: (err: unknown) => void;
  onAdvanced: () => void;
}) {
  // Pre-fill the search with the club's name — its Page is almost always a
  // name match, so the pick is usually keystroke-free.
  const [search, setSearch] = useState(accountName ?? row.name);
  const debounced = useDebouncedValue(search, 300);

  const pages = useQuery({
    queryKey: ['onboardingPages', row.id, debounced],
    queryFn: () => getOnboardingPages(row.id, { q: debounced }),
  });
  const refresh = useMutation({
    mutationFn: () => getOnboardingPages(row.id, { refresh: true }),
    onSuccess: () => void pages.refetch(),
    onError,
  });
  const select = useMutation({
    mutationFn: (pageId: string) => selectOnboardingPage(row.id, pageId),
    onSuccess: onAdvanced,
    onError: (err) => {
      onError(err);
      // An expired token regressed the row to 'created' server-side — reload so
      // the Connect button reappears.
      if (err instanceof TokenExpiredError) onAdvanced();
    },
  });

  const unavailable = pages.data?.available === false;

  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-4">
      <h3 className="text-sm font-semibold text-slate-900">Select the club's Facebook Page</h3>
      <p className="mt-0.5 text-xs text-slate-500">
        Connected via {row.metaUsername ? row.metaUsername : 'Meta'}. Search by page name or
        handle.
      </p>
      <div className="mt-2 flex items-center gap-2">
        <Input
          className="max-w-md"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search pages…"
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending || pages.isFetching}
          title="Refetch pages from Meta"
        >
          <RefreshCw className={refresh.isPending ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          Refresh
        </Button>
      </div>

      {pages.isLoading ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
          <Spinner className="h-4 w-4" /> Loading pages…
        </div>
      ) : pages.isError ? (
        <p className="mt-3 text-sm text-rose-600">
          {pages.error instanceof Error ? pages.error.message : 'Failed to load pages.'}
        </p>
      ) : unavailable ? (
        <div className="mt-3 text-sm text-slate-600">
          <p>Couldn't load your Facebook pages.</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
          >
            {refresh.isPending ? 'Retrying…' : 'Retry'}
          </Button>
        </div>
      ) : (
        <>
          <ul className="mt-2 max-h-64 max-w-xl divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200 bg-white">
            {(pages.data?.pages ?? []).map((p) => (
              <li key={p.id} className="flex items-center gap-3 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-slate-900">{p.name}</p>
                  <p className="text-xs text-slate-500">
                    {p.username ? `@${p.username}` : p.id}
                    {p.category ? ` · ${p.category}` : ''}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={blocked || select.isPending}
                  onClick={() => select.mutate(p.id)}
                >
                  {select.isPending && select.variables === p.id ? 'Selecting…' : 'Use this'}
                </Button>
              </li>
            ))}
            {(pages.data?.pages ?? []).length === 0 && (
              <li className="px-3 py-2 text-sm text-slate-500">No pages match.</li>
            )}
          </ul>
          {pages.data && pages.data.total > pages.data.pages.length && (
            <p className="mt-1 text-xs text-slate-400">
              Showing {pages.data.pages.length} of {pages.data.total} — refine your search.
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ---------- Step 3: ad-account scoping + finalize ----------

function Step3Panel({
  row,
  blocked,
  onError,
  onAdvanced,
}: {
  row: AccountOnboarding;
  blocked: boolean;
  onError: (err: unknown) => void;
  onAdvanced: () => void;
}) {
  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-4">
      {row.status === 'connected' ? (
        <AdAccountPicker row={row} blocked={blocked} onError={onError} onAdvanced={onAdvanced} />
      ) : (
        <FinalizeForm row={row} blocked={blocked} onError={onError} onAdvanced={onAdvanced} />
      )}
    </div>
  );
}

function AdAccountPicker({
  row,
  blocked,
  onError,
  onAdvanced,
}: {
  row: AccountOnboarding;
  blocked: boolean;
  onError: (err: unknown) => void;
  onAdvanced: () => void;
}) {
  const [search, setSearch] = useState('');
  const adAccounts = useQuery({
    queryKey: ['onboardingAdAccounts', row.id],
    queryFn: () => getOnboardingAdAccounts(row.id),
  });
  const select = useMutation({
    mutationFn: (adAccountId: string) => selectOnboardingAdAccount(row.id, adAccountId),
    onSuccess: onAdvanced,
    onError,
  });

  const filtered = (adAccounts.data?.adAccounts ?? []).filter(
    (a) =>
      a.name.toLowerCase().includes(search.trim().toLowerCase()) ||
      a.id.includes(search.trim()),
  );

  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-900">Select the club's ad account</h3>
      <p className="mt-0.5 text-xs text-slate-500">
        Ad sync is scoped to this single ad account — agency tokens can see many, so pick
        carefully.
      </p>
      <Input
        className="mt-2 max-w-md"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by name or act_ id…"
      />
      {adAccounts.isLoading ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
          <Spinner className="h-4 w-4" /> Loading ad accounts…
        </div>
      ) : adAccounts.isError ? (
        <p className="mt-3 text-sm text-rose-600">
          {adAccounts.error instanceof Error ? adAccounts.error.message : 'Failed to load.'}
        </p>
      ) : (
        <ul className="mt-2 max-h-64 max-w-xl divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200 bg-white">
          {filtered.map((a) => (
            <li key={a.id} className="flex items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-slate-900">{a.name}</p>
                <p className="text-xs text-slate-500">
                  {a.id}
                  {a.currency ? ` · ${a.currency}` : ''}
                  {a.status ? ` · ${a.status}` : ''}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={blocked || select.isPending}
                onClick={() => select.mutate(a.id)}
              >
                {select.isPending && select.variables === a.id ? 'Scoping…' : 'Use this'}
              </Button>
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="px-3 py-2 text-sm text-slate-500">No ad accounts match.</li>
          )}
        </ul>
      )}
    </div>
  );
}

function FinalizeForm({
  row,
  blocked,
  onError,
  onAdvanced,
}: {
  row: AccountOnboarding;
  blocked: boolean;
  onError: (err: unknown) => void;
  onAdvanced: () => void;
}) {
  const resources = useQuery({
    queryKey: ['onboardingResources', row.id],
    queryFn: () => getOnboardingResources(row.id),
  });

  const [pageId, setPageId] = useState<string | null>(null);
  const [useInstagram, setUseInstagram] = useState(true);
  const [pixelId, setPixelId] = useState('');
  const [leadFormId, setLeadFormId] = useState('');

  const pages = resources.data?.pages ?? [];
  const selectedPageId = pageId ?? pages.find((p) => p.isSelected)?.id ?? pages[0]?.id ?? null;
  const selectedPage = pages.find((p) => p.id === selectedPageId) ?? null;
  const instagram = resources.data?.instagram ?? null;

  const finalize = useMutation({
    mutationFn: (body: FinalizeBody) => finalizeOnboarding(row.id, body),
    onSuccess: onAdvanced,
    onError,
  });

  if (resources.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Spinner className="h-4 w-4" /> Loading pages, pixels and lead forms…
      </div>
    );
  }
  if (resources.isError) {
    return (
      <p className="text-sm text-rose-600">
        {resources.error instanceof Error ? resources.error.message : 'Failed to load resources.'}
      </p>
    );
  }

  const err = resources.data?.errors;

  return (
    <div className="max-w-xl space-y-4">
      <h3 className="text-sm font-semibold text-slate-900">
        Finalize — link the Zernio profile to the account
      </h3>

      <Field label="Facebook Page" hint={err?.pages ?? undefined}>
        <select
          className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
          value={selectedPageId ?? ''}
          onChange={(e) => setPageId(e.target.value || null)}
          disabled={pages.length === 0}
        >
          {pages.length === 0 && <option value="">No pages found</option>}
          {pages.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.isSelected ? ' (selected in Zernio)' : ''}
            </option>
          ))}
        </select>
      </Field>

      {instagram && (
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={useInstagram}
            onChange={(e) => setUseInstagram(e.target.checked)}
          />
          Use Instagram {instagram.username ? `@${instagram.username}` : instagram.id} as the
          account's default IG identity
        </label>
      )}

      <Field label="Pixel (optional)" hint={err?.pixels ?? undefined}>
        <select
          className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
          value={pixelId}
          onChange={(e) => setPixelId(e.target.value)}
        >
          <option value="">None</option>
          {(resources.data?.pixels ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.id})
            </option>
          ))}
        </select>
      </Field>

      <Field label="Default lead form (optional)" hint={err?.leadForms ?? undefined}>
        <select
          className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
          value={leadFormId}
          onChange={(e) => setLeadFormId(e.target.value)}
        >
          <option value="">None</option>
          {(resources.data?.leadForms ?? []).map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
      </Field>

      <div className="flex items-center gap-2">
        <Button
          disabled={blocked || finalize.isPending}
          onClick={() =>
            finalize.mutate({
              pageId: selectedPageId,
              pageName: selectedPage?.name ?? null,
              instagramAccountId: useInstagram && instagram ? instagram.id : null,
              instagramAccountName:
                useInstagram && instagram?.username ? `@${instagram.username}` : null,
              pixelId: pixelId || null,
              pixelName:
                (pixelId && resources.data?.pixels?.find((p) => p.id === pixelId)?.name) || null,
              defaultLeadFormId: leadFormId || null,
              leadFormName:
                (leadFormId &&
                  resources.data?.leadForms?.find((f) => f.id === leadFormId)?.name) ||
                null,
            })
          }
        >
          {finalize.isPending ? 'Linking account…' : 'Finalize & link account'}
        </Button>
        {finalize.isError && !(finalize.error instanceof PaymentRequiredError) && (
          <p className="text-sm text-rose-600">
            {finalize.error instanceof Error ? finalize.error.message : 'Finalize failed.'}
          </p>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-700">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-amber-600">{hint}</p>}
    </div>
  );
}
